import type { BidArtifact, BidTodo } from '@/lib/api'

/**
 * The only three fields routing depends on. Widened from `BidArtifact` so
 * callers holding a lighter shape (a `BidTodo`, say) can route without
 * fabricating a whole artifact.
 */
export type ResponseTarget = Pick<BidArtifact, 'id' | 'artifactType' | 'scopeKind'>

export function getResponseTargetPath(
  artifact: ResponseTarget,
  bidId: string,
  sectionKey?: string | null
): string {
  if (artifact.artifactType === 'questionnaire') {
    return sectionKey
      ? `/app/bids/${bidId}/responses/${artifact.id}/questionnaire/${sectionKey}`
      : `/app/bids/${bidId}/responses/${artifact.id}/questionnaire`
  }

  // Type BEFORE scope: an excel_schedule is edited on the schedule surface
  // whatever its scope, and `scopeKind === 'artifact'` used to swallow it into
  // /document, which has no grid to render.
  if (artifact.artifactType === 'excel_schedule') {
    return `/app/bids/${bidId}/responses/${artifact.id}/schedule`
  }

  if (artifact.scopeKind === 'artifact') {
    return `/app/bids/${bidId}/responses/${artifact.id}/document`
  }

  return sectionKey
    ? `/app/bids/${bidId}/responses/${artifact.id}/sections/${sectionKey}`
    : `/app/bids/${bidId}/responses/${artifact.id}`
}

/**
 * Where a `[TODO]` marker lives, as a route.
 *
 * `sectionId` is the draft segment's own id and is NOT a route key — a prose
 * draft is segmented by sub-heading, so it is usually a slug the outline never
 * names, and routing on it lands the user on "Section not found". The server
 * resolves the segment back to its outline section as `outlineSectionKey`;
 * when that is null nothing in the outline matched, so the link drops to the
 * artifact itself — somewhere real, if less precise.
 *
 * The editor family is still decided by the artifact's type and scope, not
 * assumed to be prose.
 */
export function getTodoTargetPath(todo: BidTodo, bidId: string): string {
  return getResponseTargetPath(
    { id: todo.artifactId, artifactType: todo.artifactType, scopeKind: todo.scopeKind },
    bidId,
    todo.outlineSectionKey ? encodeURIComponent(todo.outlineSectionKey) : null
  )
}
